/**
 * One task of the timeline chart beyond its bar: the contract fields the task popover needs (the
 * real ids and two optional display texts), the popover's brief (dates, overdue, assignee), and when
 * its row has scrolled out of view.
 * Decided with Khalid 2026-10-13; showcase: src/assets/showcase/ai-chat-client-timeline-showcase.html
 * (task popover, "Mid" content). Drawn by components/parts/TaskPopover.vue; where "Open in task board"
 * goes is the shared action's (src/actions/openTaskOnBoard.js).
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { ELLIPSIS } from './chartBasics.js';
import { dayNumber, yearOf, formatDay } from './timelineScale.js';

/** Ids go into a route: letters, digits, '-' and '_' only (GUIDs and numbers pass), and not too long. */
const SAFE_ID = /^[A-Za-z0-9_-]{1,64}$/;
const ID_FIELDS = Object.freeze(['taskId', 'taskTypeId', 'facilityId']);
/** Longest optional text kept (characters); the popover clamps the description to two lines anyway. */
export const TASK_TEXT_MAX = Object.freeze({ description: 280, tag: 64 });
export const UNASSIGNED = 'Unassigned';
export const OPEN_IN_TASK_BOARD = 'Open in task board';
const ARROW = '→';

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

function cleanId(value) {
  const v = typeof value === 'number' && Number.isFinite(value) ? String(value) : value;
  if (typeof v !== 'string') return null;
  const s = v.trim();
  return SAFE_ID.test(s) ? s : null;
}

function cleanText(value, max) {
  if (typeof value !== 'string') return null;
  const s = value.replace(/\s+/g, ' ').trim();
  if (!s) return null;
  return s.length > max ? `${s.slice(0, max - 1).trimEnd()}${ELLIPSIS}` : s;
}

/**
 * The item fields beyond drawing, cleaned; only the ones present and valid are returned.
 * @param {object} raw a timeline item from the server
 * @returns {{ taskId?: string, taskTypeId?: string, facilityId?: string, description?: string, tag?: string }}
 */
export function taskFields(raw) {
  const out = {};
  ID_FIELDS.forEach((key) => {
    const id = cleanId(raw && raw[key]);
    if (id) out[key] = id;
  });
  const description = cleanText(raw && raw.description, TASK_TEXT_MAX.description);
  const tag = cleanText(raw && raw.tag, TASK_TEXT_MAX.tag);
  if (description) out.description = description;
  if (tag) out.tag = tag;
  return out;
}

/**
 * "29 Sep → 10 Oct · 12 days" (inclusive), or "Due 12 Nov" for a due-only task. A date in another
 * year than today shows its year.
 * @param {{ start: string|null, end: string }} item a normalised timeline item
 * @param {number} today UTC day number
 */
export function taskDatesText(item, today) {
  const day = (n) => formatDay(n, yearOf(n) !== yearOf(today));
  const end = dayNumber(item.end);
  if (!item.start) return `Due ${day(end)}`;
  const start = dayNumber(item.start);
  return `${day(start)} ${ARROW} ${day(end)} · ${plural(end - start + 1, 'day')}`;
}

/**
 * "Overdue by 3 days", or "Overdue" when the server flags it on (or before) the due day. Null when
 * not overdue: the server decides overdue, never the dates.
 */
export function overdueText(item, today) {
  if (!item.overdue) return null;
  const late = today - dayNumber(item.end);
  return late > 0 ? `Overdue by ${plural(late, 'day')}` : 'Overdue';
}

/**
 * What the task popover shows ("Mid" content): title, status, display ref, dates, assignee, and the
 * overdue, description and tag lines only when they apply.
 * @param {object} item a normalised timeline item
 * @param {number} today UTC day number
 */
export function taskBrief(item, today) {
  return {
    title: item.label,
    ref: item.id && item.id !== item.label ? item.id : null,
    status: item.status,
    state: item.state,
    dates: taskDatesText(item, today),
    assignee: item.assignee || UNASSIGNED,
    overdue: overdueText(item, today),
    description: item.description || null,
    tag: item.tag || null,
  };
}

/**
 * The vertical band visible through every box around a row (scroll boxes, the timeline body under
 * its sticky header). Null when there is no box.
 * @param {{ top: number, bottom: number }[]} bands
 */
export function intersectBands(bands) {
  if (!bands.length) return null;
  return bands.reduce((a, b) => ({ top: Math.max(a.top, b.top), bottom: Math.min(a.bottom, b.bottom) }));
}

/** A row is in view while its middle is inside the band: half under the header counts as gone. */
export function isRowInView(row, band) {
  if (!band) return true;
  const middle = (row.top + row.bottom) / 2;
  return middle >= band.top && middle <= band.bottom;
}
